import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

function AboutUs() {
  const { t } = useTranslation();

  return (
    <section className="min-h-screen pt-24 md:pt-28 pb-12 px-4 sm:px-6 lg:px-8">
      {/* Background effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/3 -right-32 w-96 h-96 bg-accent-pink/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-5xl mx-auto"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="text-center mb-12">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent-pink/10 border border-accent-pink/20 rounded-full text-accent-pink text-sm font-medium mb-6"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('header.aboutUs')}
          </motion.div>
          <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
            {t('WhoWeAre.title')}
          </h1>
        </motion.div>

        {/* Content Grid */}
        <motion.div variants={containerVariants} className="grid gap-6 md:grid-cols-2">
          {/* About Card */}
          <motion.div variants={itemVariants} className="md:col-span-2 glass-card p-6 md:p-8">
            <p className="text-text-secondary leading-relaxed text-lg">
              {t('WhoWeAre.informations')}
            </p>
          </motion.div>

          {/* Address Card */}
          <motion.div variants={itemVariants} className="glass-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-text-primary">{t('WhoWeAre.Address')}</h3>
            </div>
            <div className="space-y-2 text-text-secondary">
              <p className="font-medium text-text-primary">IFPB Campus Sousa - Sede</p>
              <p>{t('WhoWeAre.city')}</p>
              <p>{t('WhoWeAre.Street')}</p>
              <p>{t('WhoWeAre.ZIP')} 58805-345</p>
            </div>
          </motion.div>

          {/* Contact Card */}
          <motion.div variants={itemVariants} className="glass-card p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-accent/10 border border-accent/20 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-text-primary">{t('WhoWeAre.Contacts')}</h3>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-text-muted mb-1">Telefone</p>
                <p className="text-text-secondary">{t('WhoWeAre.Phone')}</p>
              </div>
              <div>
                <p className="text-sm text-text-muted mb-1">Email</p>
                <a
                  href="mailto:greenmakerifpb@gmail.com"
                  className="text-primary hover:text-primary-light transition-colors"
                >
                  greenmakerlab@ifpb.edu.br
                </a>
              </div>
            </div>
          </motion.div>

          {/* Map Card */}
          <motion.div variants={itemVariants} className="md:col-span-2 glass-card p-6 overflow-hidden">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-accent-pink/10 border border-accent-pink/20 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-accent-pink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-text-primary">{t('WhoWeAre.Location')}</h3>
            </div>
            <div className="relative rounded-xl overflow-hidden border border-glass-border">
              <iframe
                className="w-full h-80 md:h-96"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3961.9174361298847!2d-38.235355929762534!3d-6.779902751286702!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x7a45b887beb6173%3A0x52b84e871806379d!2sIFPB%20-%20Campus%20Sousa!5e0!3m2!1spt-BR!2sbr!4v1724121094456!5m2!1spt-BR!2sbr"
                allowFullScreen=""
                loading="lazy"
                title="Localizacao IFPB Campus Sousa"
              />
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}

export default AboutUs;
